// Suggestions queue — what the agent has proposed and is waiting for
// the designer to act on. Content data; mutable client-side state lives
// in localStorage so an accept / reject survives a reload.

export type SuggestionAction = "substitute" | "redistribute" | "restyle" | "approval-packet" | "flag";

export interface AgentSuggestion {
  id: string;
  kind: SuggestionAction;
  title: string;
  rationale: string;
  firedByRuleId: string;
  affectsNodeIds: string[];
  delta?: { label: string; tone: "money" | "time" | "style" }[];
  /** Optional one-line preview of what would change in the graph. */
  preview?: string;
  /** Suggested by the agent at this point in history (relative ts). */
  proposedAgo: string;
}

export const SEED_SUGGESTIONS: AgentSuggestion[] = [
  {
    id: "sg-1",
    kind: "substitute",
    title: "Substitute Pendant L-887 → Pendant L-901",
    rationale:
      "92-day lead breaks the Lighting install milestone (18 Jul). Memory Card mc1 (7 accepts / 1 reject) supports this exact swap. L-901: 14-day lead · 88% style match · ΔPrice +₹7,000.",
    firedByRuleId: "R-lead-vs-milestone",
    affectsNodeIds: ["s2", "s2b", "ms3"],
    delta: [
      { label: "−78 days lead", tone: "time" },
      { label: "+₹7,000", tone: "money" },
      { label: "88% style", tone: "style" },
    ],
    preview: "Replace SKU s2 with s2b · update BudgetLine b2 by +₹7,000",
    proposedAgo: "2 min ago",
  },
  {
    id: "sg-2",
    kind: "redistribute",
    title: "Move ₹40,000 from Bedroom storage → Kitchen counter",
    rationale:
      "Kitchen modular at 25.3% of budget cap (target ≤ 25%). Solver suggests redistribution; style cohesion drops 1.4 pp, inside your 5-pp tolerance.",
    firedByRuleId: "R-budget-kitchen-25",
    affectsNodeIds: ["b3", "b4"],
    delta: [
      { label: "fits 25% cap", tone: "money" },
      { label: "cohesion −1.4 pp", tone: "style" },
    ],
    preview: "BudgetLine b4 −₹40k · BudgetLine b3 +₹40k",
    proposedAgo: "12 min ago",
  },
  {
    id: "sg-3",
    kind: "restyle",
    title: "Restyle Bunk bed K-220 before render",
    rationale:
      "Kids-bedroom cohesion 0.79 (threshold 0.82); K-220 is the outlier at 0.79 vs Scandinavian-Indo. Memory Card mc4 saved $650 / project on this exact pattern.",
    firedByRuleId: "R-room-cohesion",
    affectsNodeIds: ["s4", "r4", "it1"],
    delta: [
      { label: "$25 render avoided", tone: "money" },
      { label: "+0.06 cohesion", tone: "style" },
    ],
    preview: "Stage one restyle pass on s4 before Take Render fires",
    proposedAgo: "33 min ago",
  },
  {
    id: "sg-4",
    kind: "approval-packet",
    title: "Draft RERA filing refresh packet",
    rationale:
      "Approval a3 (GJ-2023-441) approved 92 days ago. Handover (30 Aug) depends on a refresh under your studio's policy.",
    firedByRuleId: "R-rera-required",
    affectsNodeIds: ["a3", "ms4"],
    delta: [{ label: "−18 days slip risk", tone: "time" }],
    preview: "Generate Markdown packet · queue for client signature",
    proposedAgo: "1 hr ago",
  },
  {
    id: "sg-5",
    kind: "flag",
    title: "Kitchen palette approval is ageing",
    rationale:
      "Approval a1 has been open 4 days; Rule R-approval-age fires at 5 d. Either escalate to PM or send a nudge.",
    firedByRuleId: "R-approval-age",
    affectsNodeIds: ["a1"],
    delta: [{ label: "PM escalation in 1 d", tone: "time" }],
    proposedAgo: "yesterday",
  },
];
